import { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { assetKeys, discoveryKeys, tagKeys } from '@/shared/lib/query/queryKeys';

import { deleteTagApi } from '@/features/tags/api';
import type { DeleteTagRequest } from '@/features/tags/api';

type DeleteTagContext = {
  tagFqn?: string;
  classification?: string;
};

type UseDeleteTagOptions = {
  onTreeRefresh?: (parentFqn: string) => void;
};

export const useDeleteTag = (tagFqn?: string, options?: UseDeleteTagOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const classification = tagFqn?.split('.')[0] || '';

  const mutation = useMutation({
    mutationFn: (request: DeleteTagRequest) => deleteTagApi(request),
    onMutate: async (): Promise<DeleteTagContext> => {
      if (tagFqn) {
        await queryClient.cancelQueries({ queryKey: tagKeys.tagDetail(tagFqn) });
        queryClient.removeQueries({ queryKey: tagKeys.tagDetail(tagFqn) });
      }

      return { tagFqn, classification };
    },
    onSuccess: (_data, _variables, context) => {
      const parentClassification = context?.classification || '';

      if (parentClassification) {
        navigate(`${ROUTES.TAGS}/${parentClassification}`, { replace: true });
      } else {
        navigate(ROUTES.TAGS, { replace: true });
      }

      optionsRef.current?.onTreeRefresh?.(parentClassification);

      queryClient.invalidateQueries({ queryKey: tagKeys.tags(parentClassification) });
      queryClient.invalidateQueries({ queryKey: tagKeys.categories() });
      queryClient.invalidateQueries({ queryKey: tagKeys.categoryDetail(parentClassification) });
      queryClient.invalidateQueries({ queryKey: tagKeys.tagsByCategory(parentClassification) });
      queryClient.invalidateQueries({ queryKey: tagKeys.availableTags() });

      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfoPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.dataAssetsPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.schemaPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.lineagePrefix() });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });

      notification.success({
        message: NOTIFICATION_MESSAGES.TAG_DELETE_SUCCESS,
        description: NOTIFICATION_MESSAGES.TAG_DELETE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleDelete = useCallback(
    (tagId: string, options?: { recursive?: boolean; hardDelete?: boolean }) => {
      return mutation.mutateAsync({
        tagId,
        recursive: options?.recursive ?? true,
        hardDelete: options?.hardDelete ?? true,
      });
    },
    [mutation]
  );

  return {
    ...mutation,
    handleDelete,
  };
};
